<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref} from "vue";
import {useI18n} from "vue-i18n";
import {useStore} from "vuex";
import ItemImage from "@/components/itemImage.vue";

const {t} = useI18n();
const store = useStore();

const props = defineProps(["session"]);
const emit = defineEmits(["patchSet", "addPerson", "removePerson", "addItemToPerson"]);

const teaserItemImage = ref<number>(0)
const teaserItemImagePos = ref<number>(1)

const pageStr = 'components.itemV2.itemV2ShopTheLook'

const itemRefs = ref<HTMLElement[]>([]);
let observer: IntersectionObserver | null = null;

const setItemRef = (el: any) => {
  if (el) {
    if (!itemRefs.value.includes(el)) {
      itemRefs.value.push(el);
      observer?.observe(el);
    }
  }
};

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
        const itemIdx = entry.target.getAttribute("data-item-idx");
        if (itemIdx !== null) {
          teaserItemImage.value = parseInt(itemIdx);
          teaserItemImagePos.value = 1;
        }
      }
    });
  }, {
    root: null,
    rootMargin: '-20% 0px -20% 0px',
    threshold: [0, 0.3, 0.5]
  });
});

onUnmounted(() => {
  observer?.disconnect();
});

const teaserImage = computed(() => {
  if (props.session.items[teaserItemImage.value] && props.session.items[teaserItemImage.value].image.positions[teaserItemImagePos.value]) {
    return props.session.items[teaserItemImage.value].image.positions[teaserItemImagePos.value].filename || ""
  } else {
    return ""
  }
})

const totalPrice = computed(() => {
  let total = 0
  if (props.session.items) {
    for (const itemIdx in props.session.items) {
      total += props.session.items[itemIdx].price.total
    }
  }
  return total
})

const patchSet = (variantValueData: { value: string, itemIdx: { [key: string]: string } }) => {

  const items: { itemIdx: string, valueIdx: string }[] = []

  for (const itemIdx in variantValueData.itemIdx) {
    items.push({
      itemIdx: parseInt(itemIdx.replace("itemIdx:", "") as string),
      valueIdx: variantValueData.itemIdx[itemIdx]
    })
  }

  emit("patchSet", {
    value: variantValueData.value,
    items: items
  })
}

const extractVariants = (searchIdx: string) => {
  const rawList = {}
  const finalList = {}
  const numberOfItems = Object.keys(props.session.items).length

  if (props.session.items) {
    for (const itemIdx in props.session.items) {
      for (const variantIdx in props.session.items[itemIdx]["variants"]) {
        if (
            props.session.items[itemIdx]["variants"][variantIdx].idx == searchIdx ||
            props.session.items[itemIdx]["variants"][variantIdx].idx.includes("." + searchIdx)
        ) {

          for (const colorIdx in props.session.items[itemIdx]["variants"][variantIdx].values) {
            const valueIdx = props.session.items[itemIdx]["variants"][variantIdx].values[colorIdx].idx as string
            if (!rawList[valueIdx]) {
              rawList[valueIdx] = {
                "title": props.session.items[itemIdx]["variants"][variantIdx].values[colorIdx].title,
                "image": props.session.items[itemIdx]["variants"][variantIdx].values[colorIdx].image,
                "order": props.session.items[itemIdx]["variants"][variantIdx].values[colorIdx].order,
                "match": props.session.items[itemIdx]["variants"][variantIdx].values[colorIdx].match,
                "value": valueIdx,
                "itemIdx": {}
              }
            }

            rawList[valueIdx]["itemIdx"]["itemIdx:" + itemIdx] = props.session.items[itemIdx]["variants"][variantIdx].idx
          }
        }
      }
    }
  }

  /**
   * check if color is possible in every item
   */

  for (const valueIdx in rawList) {
    if (Object.keys(rawList[valueIdx]["itemIdx"]).length === numberOfItems) {
      finalList[valueIdx] = rawList[valueIdx]
    }
  }

  return finalList
}

const setVariants = computed(() => {
  return {
    color: extractVariants("color"),
    metal: extractVariants("metal"),
    stone_type: extractVariants("stone_type"),
    stone_quality: extractVariants("stone_quality")
  }
})

const variantsText = computed(() => {
  return {
    color: t(pageStr + '.color'),
    metal: t(pageStr + '.metal'),
    stone_type: t(pageStr + '.stone_type'),
    stone_quality: t(pageStr + '.stone_quality')
  }
})

</script>

<template>
  <div class="flex flex-col lg:flex-row w-full min-h-screen bg-white">
    <!-- Sticky Left Banner -->
    <div class="w-full lg:w-5/12 h-[50vh] lg:h-screen lg:sticky top-0 overflow-hidden bg-gray-50">
      <div class="relative w-full h-full">
        <transition name="fade" mode="out-in">
          <template v-if="teaserImage">
            <img
                v-if="teaserImage.includes('.webp')"
                :key="teaserImage"
                :src="teaserImage"
                class="object-cover h-full w-full"
                alt="Teaser"
            />
            <video v-else :key="'video-' + teaserImage" class="object-cover h-full w-full" autoplay loop muted playsinline>
              <source :src="teaserImage" type="video/mp4">
              {{ $t(pageStr + '.your_browser_does_not_support_the') }}
            </video>
          </template>
        </transition>
        <!-- Overlay for better text readability if needed -->
        <div class="absolute inset-0 bg-gradient-to-r from-black/10 to-transparent pointer-events-none"></div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="w-full lg:w-7/12 p-6 lg:p-12">
      <!-- Global Configuration Header -->
      <nav class="sticky top-4 z-50 bg-white/90 backdrop-blur-md px-6 py-6 border border-gray-100 mb-12 rounded-3xl shadow-xl shadow-gray-200/50">
        <div class="flex flex-col xl:flex-row xl:items-center justify-between gap-8">
          <div class="flex flex-col">
            <h2 class="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-1">
              {{ $t(pageStr + '.customize_your_set') }}
            </h2>
            <div class="flex items-baseline gap-3">
              <span class="text-3xl font-black text-brand-main tracking-tight">{{ $n(totalPrice, "currency") }}</span>
              <span class="text-xs text-gray-500 font-semibold">{{ $t(pageStr + '.set_price') }}</span>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-x-8 gap-y-6">
            <template v-for="(variantList, variantIdx) in setVariants" :key="variantIdx">
              <div v-if="Object.keys(variantList).length > 0" class="flex flex-col gap-2.5">
                <span class="text-[10px] font-extrabold text-gray-400 uppercase tracking-widest">{{ variantsText[variantIdx] }}</span>

                <!-- Stone Type Toggle -->
                <div v-if="variantIdx == 'stone_type'" class="flex items-center rounded-full bg-gray-100 p-1 ring-1 ring-gray-200">
                  <button
                      v-for="(typeKey, typeIdx) in ['earth', 'lab']"
                      :key="typeKey + typeIdx"
                      type="button"
                      class="min-w-[80px] rounded-full px-4 py-1.5 text-[10px] font-bold tracking-wider transition-all duration-300 focus:outline-none"
                      :class="setVariants['stone_type'][typeKey]?.match ? 'bg-brand-main text-white shadow-lg shadow-brand-main/20 ring-1 ring-brand-main' : 'text-gray-500 hover:text-gray-700'"
                      @click="patchSet(setVariants['stone_type'][typeKey])"
                  >
                    {{ $t(pageStr + '.' + (typeKey === 'earth' ? 'natural' : 'lab')) }}
                  </button>
                </div>

                <!-- Other Variants (Metal, Quality, Color) -->
                <div v-else class="flex items-center gap-2.5">
                  <div v-for="(valueData, valueIdx) in variantList" :key="valueIdx"
                       class="cursor-pointer transition-all duration-300 relative group"
                       @click="patchSet(valueData)"
                  >
                    <!-- Text Options (Metal, Stone Quality) -->
                    <div v-if="variantIdx == 'metal' || variantIdx == 'stone_quality'"
                         class="px-3 py-1.5 rounded-xl text-[10px] font-black border transition-all duration-300"
                         :class="valueData.match ? 'bg-brand-main text-white border-brand-main shadow-lg shadow-brand-main/20 transform scale-105' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'"
                    >
                      {{ valueData.value.toUpperCase() }}
                    </div>
                    <!-- Color Swatches -->
                    <div v-else class="relative p-0.5 rounded-full border-2 transition-all duration-300"
                         :class="valueData.match ? 'border-brand-main shadow-lg shadow-brand-main/20 transform scale-110' : 'border-transparent hover:border-gray-300'"
                    >
                      <img
                          :src="'https://rubin-website-images.s3.eu-central-1.amazonaws.com/assets/colors/' + valueData.value.toLowerCase() + '@2x.jpg'"
                          alt=""
                          class="rounded-full object-cover h-6 w-6 border border-gray-100"
                      >
                      <!-- Tooltip -->
                      <span class="absolute -top-10 left-1/2 -translate-x-1/2 px-2 py-1 bg-brand-main text-white text-[10px] rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[60] shadow-xl">
                        {{ valueData.title }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </nav>

      <!-- Item List grouped by Person -->
      <div v-for="(person, personIdx) in session.persons" :key="'person' + personIdx" class="mb-20">
        <div class="flex items-center gap-4 mb-8">
          <div class="h-px flex-grow bg-gray-100"></div>
          <h3 class="text-xs font-black text-gray-400 uppercase tracking-[0.3em] flex items-center gap-3">
             <span class="w-6 h-6 rounded-full bg-brand-main text-white flex items-center justify-center text-[10px]">
               {{ personIdx + 1 }}
             </span>
            {{ person.name || $t(pageStr + '.person_n', { n: personIdx + 1 }) }}
          </h3>
          <div class="h-px flex-grow bg-gray-100"></div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div v-for="itemIdx in person.items" :key="'person' + personIdx + 'Item' + itemIdx"
               :ref="setItemRef"
               :data-item-idx="itemIdx"
               @mouseover="teaserItemImage=itemIdx; teaserItemImagePos=1"
               class="group relative flex flex-col bg-white rounded-[2.5rem] p-5 transition-all duration-500 hover:shadow-2xl hover:shadow-gray-200/50 border border-gray-50">

            <div class="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-gray-50 mb-6">
              <router-link :to="'/conf/' + session.session_id + '/' + itemIdx"
                           @click="store.commit('session/setViewMode', 'item'); store.commit('session/setItemIdx', itemIdx)"
                           class="block w-full h-full">
                <item-image
                    :image-url="session.items[itemIdx].image.filename"
                    :image-layer-url="session.items[itemIdx].image.filename_layer"
                    :main-metal="session.items[itemIdx].image.main_metal"
                    :zoom="false"
                    class="w-full h-full object-contain transition-transform duration-1000 group-hover:scale-110"
                />
                <div class="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </router-link>

              <!-- Quick action overlay -->
              <div class="absolute top-5 right-5 flex flex-col gap-3 opacity-0 translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">
                <router-link :to="'/conf/' + session.session_id + '/' + itemIdx"
                             @click="store.commit('session/setViewMode', 'item'); store.commit('session/setItemIdx', itemIdx)"
                             class="p-3 bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl hover:bg-brand-main hover:text-white transition-all duration-300">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </router-link>
              </div>
            </div>

            <div class="px-2 flex-grow flex flex-col">
              <h4 class="text-lg font-bold text-gray-900 mb-2 leading-tight group-hover:text-brand-main transition-colors">
                {{ session.items[itemIdx].desc.title }}
              </h4>

              <div class="mt-auto pt-4 flex justify-between items-end">
                <div class="flex flex-col">
                  <span class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{{ $t(pageStr + '.price') }}</span>
                  <span class="text-xl font-black text-brand-main">{{ $n(session.items[itemIdx].price.total, "currency") }}</span>
                </div>

                <router-link :to="'/conf/' + session.session_id + '/' + itemIdx"
                             @click="store.commit('session/setViewMode', 'item'); store.commit('session/setItemIdx', itemIdx)"
                             class="text-[10px] font-black text-brand-main hover:opacity-60 uppercase tracking-[0.2em] flex items-center gap-2 transition-all group/link">
                  {{ $t(pageStr + '.details') }}
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 transition-transform duration-300 group-hover/link:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>